<script setup>
import { ref, onMounted, watch } from "vue";
import { useRoute } from "vue-router";
import AnHeader from "./AnHeader.vue";
import AnStatCard from "./AnStatCard.vue";
import AnCalendar from "./AnCalendar.vue";
import AnRankBars from "./AnRankBars.vue";
import AnHourHeat from "./AnHourHeat.vue";
import AnalyticsService from "@/services/analytics";
import AgentService from "@/services/agent";
import { useCompanyStore } from "@/stores/company";

const companyStore = useCompanyStore();
const route = useRoute();

const COLORS = { accent: "#38bdf8", good: "#34d399", warn: "#fbbf24", bad: "#fb7185", accent2: "#8b7cf6" };
const PALETTE = [COLORS.accent, COLORS.good, COLORS.warn, COLORS.accent2, COLORS.bad];

// ── state ────────────────────────────────────────────────────────
const period = ref("daily");
const dateRange = ref(null);
const loading = ref(false);
const deviceId = ref(route.params.deviceId || null);
const topDevices = ref([]); // [{ uuid, label, count }]

const stats = ref({ conversations: 0, messages: 0, avgLength: 0, attachments: 0 });
const activeDaysIndices = ref([]);
const agentRows = ref([]);
const hourValues = ref(Array(24).fill(0));
const roleBreakdown = ref({ user: 0, assistant: 0, system: 0 });
const firstSeen = ref("");
const lastSeen = ref("");

// ── build top-devices list from all agents' device_breakdown ─────
async function fetchTopDevices() {
  const companyId = companyStore.companyId;
  if (!companyId) return;
  try {
    const agentsRes = await AgentService.getAgentsGrid(companyId);
    const agents = (agentsRes?.data || []).slice(0, 6);
    if (!agents.length) return;

    const results = await Promise.all(
      agents.map((a) =>
        AnalyticsService.getAgentAnalytics(companyId, a.id, {})
          .then((res) => res?.device_breakdown || [])
          .catch(() => [])
      )
    );

    const deviceMap = new Map();
    results.flat().forEach((d) => {
      if (!d.user_uuid) return;
      if (deviceMap.has(d.user_uuid)) {
        const entry = deviceMap.get(d.user_uuid);
        entry.count += d.conversation_count ?? 0;
      } else {
        deviceMap.set(d.user_uuid, {
          uuid: d.user_uuid,
          count: d.conversation_count ?? 0,
          label: d.device_name || d.user_uuid.substring(0, 8) + "…",
        });
      }
    });

    topDevices.value = [...deviceMap.values()]
      .sort((a, b) => b.count - a.count)
      .slice(0, 12);
  } catch (e) {
    console.error("fetchTopDevices error:", e);
  }
}

// ── fetch selected device's analytics ────────────────────────────
async function fetchData() {
  const companyId = companyStore.companyId;
  if (!companyId || !deviceId.value) return;
  loading.value = true;
  try {
    const params = { period: period.value };
    if (dateRange.value?.[0] && dateRange.value?.[1]) {
      params.from_date = dateRange.value[0].toISOString();
      params.until_date = dateRange.value[1].toISOString();
    }

    const [convRes, msgRes] = await Promise.all([
      AnalyticsService.getUserConversationsAnalytics(companyId, deviceId.value, params),
      AnalyticsService.getUserMessagesAnalytics(companyId, deviceId.value, params),
    ]);

    if (convRes?.statistics) {
      const s = convRes.statistics;
      stats.value.conversations = s.total_conversations ?? 0;
      firstSeen.value = s.first_conversation
        ? new Date(s.first_conversation).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
        : "";
      lastSeen.value = s.last_conversation
        ? new Date(s.last_conversation).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
        : "";

      if (s.active_days_list?.length) {
        const base = new Date();
        base.setDate(base.getDate() - 182);
        activeDaysIndices.value = s.active_days_list
          .map((d) => { const diff = Math.floor((new Date(d) - base) / 86400000); return diff >= 0 && diff < 182 ? diff : null; })
          .filter((v) => v !== null);
      } else {
        activeDaysIndices.value = [];
      }
    }

    if (convRes?.agent_breakdown?.length) {
      agentRows.value = convRes.agent_breakdown.slice(0, 5).map((a, i) => ({
        name: a.agent_name || (a.agent_uuid?.substring(0, 10) + "…") || "Agent",
        sub: `${a.conversation_count} convos`,
        value: a.conversation_count ?? 0,
        color: PALETTE[i % PALETTE.length],
      }));
    } else {
      agentRows.value = [];
    }

    if (msgRes?.statistics) {
      const m = msgRes.statistics;
      stats.value.messages = m.total_messages ?? 0;
      stats.value.avgLength = Number(m.avg_message_length ?? 0).toFixed(1);
      stats.value.attachments = m.messages_with_attachments ?? 0;
      roleBreakdown.value = {
        user: m.user_messages ?? 0,
        assistant: m.assistant_messages ?? 0,
        system: m.system_messages ?? 0,
      };
    }

    if (msgRes?.hourly_distribution?.length) {
      const filled = Array(24).fill(0);
      msgRes.hourly_distribution.forEach((h) => { filled[h.hour] = h.message_count ?? 0; });
      hourValues.value = filled;
    }
  } catch (e) {
    console.error("DevicesBoard fetch error:", e);
  } finally {
    loading.value = false;
  }
}

// when navigated from AgentsBoard via URL (e.g. /analytics/devices/:uuid)
watch(() => route.params.deviceId, (id) => {
  if (id) deviceId.value = id;
});

// single watcher — fires when pill is clicked or URL param arrives
watch(deviceId, fetchData);
watch([period, dateRange], fetchData);

onMounted(async () => {
  loading.value = true;
  await fetchTopDevices();
  if (!deviceId.value && topDevices.value.length) {
    deviceId.value = topDevices.value[0].uuid; // triggers watch(deviceId) → fetchData
  } else if (deviceId.value) {
    await fetchData();
  } else {
    loading.value = false;
  }
});
</script>

<template>
  <div class="an-page">
    <AnHeader
      title="Devices"
      subtitle="Individual device journey, engagement cadence, and channel preferences."
      @period-change="p => { period = p.toLowerCase(); }"
      @date-change="dr => { dateRange = dr; }"
    />

    <!-- Device selector — same structure as AgentsBoard agent selector -->
    <div class="an-agent-select">
      <template v-if="loading && !topDevices.length">
        <Skeletor v-for="i in 6" :key="i" height="32" :width="`${70 + i * 12}px`" as="div" style="border-radius:20px;flex-shrink:0" />
      </template>
      <template v-else>
        <button
          v-for="d in topDevices"
          :key="d.uuid"
          class="an-agent-btn"
          :class="{ 'is-active': deviceId === d.uuid }"
          @click="deviceId = d.uuid"
        >
          {{ d.label }}
        </button>
      </template>
    </div>

    <!-- Skeleton -->
    <template v-if="loading">
      <div class="an-grid">
        <div class="an-panel an-c12">
          <div class="an-skel an-skel--row">
            <Skeletor circle width="44" height="44" />
            <div class="an-skel" style="flex:1;gap:6px">
              <Skeletor height="14" width="30%" as="div" />
              <Skeletor height="10" width="50%" as="div" />
            </div>
          </div>
        </div>
        <div v-for="i in 4" :key="i" class="an-panel an-c3">
          <div class="an-skel">
            <Skeletor height="10" width="55%" as="div" />
            <Skeletor height="32" width="50%" as="div" />
            <Skeletor height="10" width="35%" as="div" />
          </div>
        </div>
        <div class="an-panel an-c7"><div class="an-skel an-skel--chart"><Skeletor height="14" width="40%" as="div" /><Skeletor height="120" as="div" /></div></div>
        <div class="an-panel an-c5"><div class="an-skel"><Skeletor height="14" width="45%" as="div" /><Skeletor v-for="j in 4" :key="j" height="12" as="div" /></div></div>
        <div class="an-panel an-c12"><div class="an-skel an-skel--chart"><Skeletor height="14" width="35%" as="div" /><Skeletor height="36" as="div" /></div></div>
      </div>
    </template>

    <div v-else-if="!deviceId" class="an-empty">No device data available.</div>

    <div v-else class="an-grid">
      <div class="an-panel an-c12" style="padding-bottom:6px">
        <div class="an-cust">
          <div class="an-cust__avatar">{{ (deviceId || "?").charAt(0).toUpperCase() }}</div>
          <div>
            <div style="font-weight:700;font-size:14px">Device profile</div>
            <div class="an-cust__uuid">{{ deviceId }}</div>
            <div v-if="firstSeen" class="an-cust__range">{{ firstSeen }} → {{ lastSeen }}</div>
          </div>
        </div>
      </div>

      <div class="an-panel an-c3"><AnStatCard label="Conversations" :value="stats.conversations" :sub="`first: ${firstSeen || '—'}`" /></div>
      <div class="an-panel an-c3"><AnStatCard label="Messages" :value="stats.messages.toLocaleString()" sub="" /></div>
      <div class="an-panel an-c3"><AnStatCard label="Avg length" :value="stats.avgLength" sub="chars · verbosity" /></div>
      <div class="an-panel an-c3"><AnStatCard label="Attachments published" :value="stats.attachments" sub="published cards & maps" /></div>

      <div class="an-panel an-c7">
        <div class="an-panel__head"><div class="an-panel__title">Engagement cadence</div></div>
        <AnCalendar :active-days="activeDaysIndices" />
      </div>
      <div class="an-panel an-c5">
        <div class="an-panel__head"><div class="an-panel__title">Agents used</div></div>
        <AnRankBars v-if="agentRows.length" :rows="agentRows" />
        <div v-else class="an-empty-panel">No agent breakdown</div>
        <div class="an-panel__head" style="margin-top:18px"><div class="an-panel__title" style="font-size:12px">Message roles</div></div>
        <div class="an-keys" style="margin-top:10px">
          <div><i style="background:#38bdf8"></i>User <b>{{ roleBreakdown.user }}</b></div>
          <div><i style="background:#34d399"></i>Assistant <b>{{ roleBreakdown.assistant }}</b></div>
          <div><i style="background:#fbbf24"></i>System <b>{{ roleBreakdown.system }}</b></div>
        </div>
      </div>

      <div class="an-panel an-c12">
        <div class="an-panel__head"><div class="an-panel__title">When this device engages</div></div>
        <AnHourHeat :values="hourValues" rgb="139,124,246" />
      </div>
    </div>
  </div>
</template>

<style scoped>
.an-empty-panel { color: var(--an-muted); font-size: 12px; padding: 16px 0; }
.an-empty { text-align: center; padding: 40px 0; color: var(--an-muted); font-size: 13px; }

.an-cust__range { font-size: 11.5px; color: var(--an-faint); margin-top: 2px; }
</style>
